import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { getActiveConversations, getConversationsInWaiting } from '../api/api';
import {
  readContactsCacheList,
  clearChatSlotsCache,
} from '../utils/cache.js';
import { formatBrazilianPhone } from '../utils/phone';
import { resolveCustomerTag } from '../utils/customerTag';
import {
  computeConversationListsFromMerged,
  getConversationCompanyId,
  getConversationGroupKey,
  isConversationAwaitingEvaluation,
  isConversationAttending,
  isConversationOpen,
  mergeAggregatedWithConversation,
  mergeConversationsByClient,
  normalizeConversationSortMode,
  normalizeOptionalNumber,
} from '../utils/conversationUtils.js';
import { LOGOUT_EVENT, emitLogoutEvent } from '../utils/auth.js';
import {
  normalizeAuthorType,
  isAttendantMessage,
  isBotMessage,
  isClientMessage,
  isSystemMessage,
} from '../utils/chatMessage.js';
import { isConversationEvaluationActive } from '../utils/evaluationTimer.js';
import { parseTimestampToDate } from '../utils/datetime.js';
import { subscribeConversationEvents } from '../websockets/conversationsHub.js';
import { logSafeError, logSafeWarning } from '../utils/safeConsole.js';
import {
  CHAT_LIST_REQUEST_TIMEOUT_MS,
  CHAT_LIST_RETRY_LIMIT,
  chatListRetryDelay,
  fetchChatListPair,
  isRetryableChatListError,
} from '../utils/chatListRecovery.js';

const OUTBOUND_INIT_KEY = 'chat_outbound_inits';
const OUTBOUND_INIT_TTL_MS = 5 * 60 * 1000; // 5 minutos
const NON_SLOT_WS_TYPES = new Set(['attendant_presence', 'whatsapp_status']);
const WS_REFETCH_COOLDOWN_MS = 5000;
const WS_RECONNECT_REFETCH_MIN_INTERVAL_MS = 5000;
const CHAT_LIST_DEBUG_FLAG = '__BLUEZAP_CHAT_LIST_DEBUG__';
const REALTIME_LIST_SYNC_META_KEY = '__bluezapListSync';
const WS_COMPOSITION_REFRESH_TYPES = new Set([
  'conversation_created',
  'conversation_assigned',
  'conversation_closed',
  'conversation_opened',
  'conversation_reopened',
  'conversation_transferred',
  'conversation_transfer',
  'attendant_changed',
  'team_changed',
  'sector_changed',
  'queue_entered',
  'queue_left',
  'waiting_entered',
  'waiting_left',
  'evaluation_started',
  'evaluation_updated',
  'evaluation_closed',
  'chats_synced',
]);
const CUSTOMERS_CACHE_INVALIDATION_WS_TYPES = new Set([
  'conversation_created',
  'conversation_assigned',
  'conversation_closed',
]);
const MESSAGE_STATUS_VALUES = new Set([
  'sent',
  'delivered',
  'received',
  'read',
  'played',
  'deleted',
  'edited',
]);
const MESSAGE_STATUS_EVENT_TYPES = new Set([
  'message_update',
  'message_updated',
  'message_status',
  'message_status_update',
  'message_status_updated',
]);

const normalizeScopeValue = (value, fallback = 'attendant') => {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (raw === 'all') return 'all';
  if (raw === 'attendant' || raw === 'mine' || raw === 'me') return 'attendant';
  return fallback;
};

const isWaitingRouteStatus = (slot) => {
  const handling = String(slot?.handling_status ?? slot?.status ?? '').toLowerCase();
  return (
    handling.includes('waiting_attendant') ||
    handling.includes('transfer_pending') ||
    handling.includes('waiting_for_team')
  );
};

const normalizeTeamIds = (values) => {
  if (!Array.isArray(values)) return [];
  const dedup = new Set();
  values.forEach((value) => {
    const parsed = normalizeOptionalNumber(value);
    if (parsed !== null) dedup.add(parsed);
  });
  return Array.from(dedup.values()).sort((a, b) => a - b);
};

const extractConversationTeamId = (slot) => {
  if (!slot || typeof slot !== 'object') return null;
  return normalizeOptionalNumber(
    slot.team_id ??
      slot.teamId ??
      (typeof slot.team === 'object' && slot.team ? slot.team.id : null)
  );
};

const hasExplicitTeamMetadata = (slot) => {
  if (!slot || typeof slot !== 'object') return false;
  return (
    Object.prototype.hasOwnProperty.call(slot, 'team_id') ||
    Object.prototype.hasOwnProperty.call(slot, 'teamId') ||
    Object.prototype.hasOwnProperty.call(slot, 'team')
  );
};

const readOutboundInitMap = () => {
  try {
    const raw = window.localStorage.getItem(OUTBOUND_INIT_KEY);
    const data = raw ? JSON.parse(raw) : {};
    if (!data || typeof data !== 'object') return {};
    const now = Date.now();
    const next = {};
    Object.keys(data).forEach((k) => {
      const ts = Number(data[k] || 0);
      if (ts && now - ts <= OUTBOUND_INIT_TTL_MS) next[k] = ts;
    });
    if (Object.keys(next).length !== Object.keys(data).length) {
      window.localStorage.setItem(OUTBOUND_INIT_KEY, JSON.stringify(next));
    }
    return next;
  } catch {
    return {};
  }
};

// Desabilitamos o cache local de slots para evitar listas desatualizadas (especialmente fila de espera).
const readSlotsCache = () => null;
const writeSlotsCache = () => {};

const normalizeSlotWhatsappIdentifier = (value) => {
  if (value === null || value === undefined) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';

  const withoutPrefix = trimmed.replace(/^whatsapp:/i, '');
  const [rawUsername, rawDomain = ''] = withoutPrefix.split('@', 2);
  const username = String(rawUsername || '').trim().replace(/:\d+$/, '');
  const domain = String(rawDomain || '').trim().toLowerCase().replace(/^@+/, '');

  if (!username) return '';
  return domain ? `${username}@${domain}` : username;
};

const normalizeSlotPhoneDigits = (value) => {
  const normalized = normalizeSlotWhatsappIdentifier(value);
  if (!normalized) return '';
  const [username] = normalized.split('@', 1);
  return String(username || '').replace(/\D/g, '');
};

const arePhoneDigitsEquivalent = (left, right) => {
  const a = normalizeSlotPhoneDigits(left);
  const b = normalizeSlotPhoneDigits(right);
  if (!a || !b) return false;
  if (a === b) return true;
  const aTail = a.length > 11 ? a.slice(-11) : a;
  const bTail = b.length > 11 ? b.slice(-11) : b;
  if (aTail === bTail) return true;
  const aShort = aTail.slice(-10);
  const bShort = bTail.slice(-10);
  return aShort && bShort && aShort === bShort;
};

const extractSlotConversationId = (slot) =>
  slot?.id ??
  slot?.conversation_id ??
  slot?.conversationId ??
  slot?.slot_id ??
  slot?.slotId ??
  null;

const extractSlotRawWhatsapp = (slot) => {
  const candidates = [
    slot?.whatsapp,
    slot?.whatsapp_number,
    slot?.whatsappNumber,
    slot?.jid,
    slot?.remote_jid,
    slot?.remoteJid,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeSlotWhatsappIdentifier(candidate);
    if (normalized) return normalized;
  }
  return '';
};

const normalizeWsToken = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const normalizeMessageStatusToken = (value) => {
  const normalized = normalizeWsToken(value);
  if (!normalized) return '';
  if (normalized.startsWith('edited_')) return 'edited';
  if (normalized.startsWith('deleted_')) return 'deleted';
  return normalized;
};

const extractPayloadMessage = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.message && typeof payload.message === 'object') return payload.message;
  if (Array.isArray(payload.messages)) {
    return payload.messages.find((message) => message && typeof message === 'object') || null;
  }
  return null;
};

const withLocalTimestampFallback = (message) => {
  if (!message || typeof message !== 'object') return message;
  const date = parseTimestampToDate(
    message.timestamp ??
      message.created_at ??
      message.createdAt ??
      message.updated_at ??
      message.updatedAt
  );
  if (date) return message;
  return {
    ...message,
    _localTimestamp: Date.now(),
  };
};

const extractMessageConversationId = (message) =>
  message?.conversation_id ??
  message?.conversationId ??
  message?.chat_id ??
  message?.chatId ??
  null;

const isNotificationDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('notification_debug') === 'true';
  } catch {
    return false;
  }
};

const debugNotificationFlow = (event, details = {}) => {
  if (!isNotificationDebugEnabled()) return;
  console.debug('[notification-flow]', event, details);
};

const resolveNotificationMessageKey = (message) => {
  if (!message || typeof message !== 'object') return null;
  const candidates = [
    message.message_key_id,
    message.messageKeyId,
    message.message_key,
    message.messageKey,
    message.baileys_message_id,
    message.baileysMessageId,
    message.remote_id,
    message.remoteId,
    message.id,
    message.message_id,
    message.messageId,
  ];
  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
      return String(candidate);
    }
  }
  return null;
};

const extractNotificationTimestamp = (source) =>
  source?._localTimestamp ??
  source?.timestamp ??
  source?.created_at ??
  source?.createdAt ??
  source?.updated_at ??
  source?.updatedAt ??
  source?.last_message_at ??
  source?.lastMessageAt ??
  null;

const getLastSlotMessage = (slot) => {
  const messages = Array.isArray(slot?.messages) ? slot.messages : [];
  return messages.length ? messages[messages.length - 1] : null;
};

const getSlotNotificationSnapshot = (slot) => {
  const lastMessage = getLastSlotMessage(slot);
  return {
    conversation_id: extractSlotConversationId(slot),
    message_key: resolveNotificationMessageKey(lastMessage),
    timestamp:
      extractNotificationTimestamp(lastMessage) ??
      extractNotificationTimestamp(slot),
  };
};

const buildRealtimeNotificationTrace = (
  payload,
  normalizedType = normalizeWsToken(payload?.type),
  normalizedEvent = normalizeWsToken(payload?.event),
  incomingSlot = payload?.slot || payload?.conversation || null,
  incomingMessage = extractPayloadMessage(payload)
) => {
  const conversationId =
    extractMessageConversationId(incomingMessage) ??
    extractSlotConversationId(incomingSlot) ??
    payload?.conversation_id ??
    payload?.conversationId ??
    payload?.chat_id ??
    payload?.chatId ??
    null;
  return {
    event_type: normalizedType || normalizedEvent || null,
    event_name: normalizedEvent || null,
    conversation_id: conversationId,
    message_key: resolveNotificationMessageKey(incomingMessage),
    timestamp:
      extractNotificationTimestamp(incomingMessage) ??
      extractNotificationTimestamp(incomingSlot) ??
      extractNotificationTimestamp(payload),
  };
};

const logConversationListUpdates = (listName, previousList, nextList, source) => {
  if (!isNotificationDebugEnabled()) return;
  const previousById = new Map();
  (Array.isArray(previousList) ? previousList : []).forEach((slot) => {
    const snapshot = getSlotNotificationSnapshot(slot);
    if (snapshot.conversation_id !== null && snapshot.conversation_id !== undefined) {
      previousById.set(String(snapshot.conversation_id), snapshot);
    }
  });

  (Array.isArray(nextList) ? nextList : []).forEach((slot) => {
    const snapshot = getSlotNotificationSnapshot(slot);
    const conversationId = snapshot.conversation_id;
    if (conversationId === null || conversationId === undefined) return;
    const previous = previousById.get(String(conversationId));
    const changed =
      !previous ||
      previous.message_key !== snapshot.message_key ||
      previous.timestamp !== snapshot.timestamp;
    if (!changed) return;
    debugNotificationFlow('conversation_list_updated', {
      source,
      list: listName,
      conversation_id: conversationId,
      previous_message_key: previous?.message_key ?? null,
      current_message_key: snapshot.message_key ?? null,
      previous_timestamp: previous?.timestamp ?? null,
      current_timestamp: snapshot.timestamp ?? null,
    });
  });
};

const doesSlotContainConversationId = (slot, conversationId) => {
  if (conversationId === null || conversationId === undefined) return false;
  const normalizedId = String(conversationId);
  const slotId = extractSlotConversationId(slot);
  if (slotId !== null && slotId !== undefined && String(slotId) === normalizedId) return true;
  const mergedIds = Array.isArray(slot?.mergedSlotIds) ? slot.mergedSlotIds : [];
  return mergedIds.some((id) => id !== null && id !== undefined && String(id) === normalizedId);
};

const hasMessageStatusSignal = (payload, normalizedType, normalizedEvent) => {
  if (MESSAGE_STATUS_EVENT_TYPES.has(normalizedType) || MESSAGE_STATUS_EVENT_TYPES.has(normalizedEvent)) {
    return true;
  }
  const message = extractPayloadMessage(payload);
  const candidates = [
    payload?.status,
    payload?.message_status,
    payload?.messageStatus,
    payload?.ack,
    message?.status,
    message?.message_status,
    message?.messageStatus,
  ];
  return candidates.some((candidate) =>
    MESSAGE_STATUS_VALUES.has(normalizeMessageStatusToken(candidate))
  );
};

const hasCompositionRefreshHint = (payload, normalizedType, normalizedEvent) => {
  if (WS_COMPOSITION_REFRESH_TYPES.has(normalizedType) || WS_COMPOSITION_REFRESH_TYPES.has(normalizedEvent)) {
    return true;
  }
  const hint = normalizeWsToken(
    payload?.reason ??
      payload?.change_type ??
      payload?.changeType ??
      payload?.action ??
      payload?.transition ??
      ''
  );
  if (!hint) return false;
  return (
    hint.includes('transfer') ||
    hint.includes('assign') ||
    hint.includes('attendant') ||
    hint.includes('team') ||
    hint.includes('sector') ||
    hint.includes('close') ||
    hint.includes('open') ||
    hint.includes('queue') ||
    hint.includes('waiting') ||
    hint.includes('evaluation')
  );
};

const logCompositionListMove = (action, slot) => {
  if (
    typeof window === 'undefined' ||
    window.localStorage?.getItem(CHAT_LIST_DEBUG_FLAG) !== '1'
  ) {
    return;
  }
  const conversationId = extractSlotConversationId(slot);
  if (conversationId === null || conversationId === undefined) return;
  console.debug('[chat-list] composition_update', {
    action,
    conversation_id: conversationId,
    handling_status: slot?.handling_status ?? null,
    in_attendance: Boolean(slot?.in_attendance),
    status: slot?.status ?? null,
  });
};

const normalizeFetchOptions = (options = {}) => {
  const normalized =
    typeof options === 'boolean'
      ? { silent: options }
      : options && typeof options === 'object'
        ? options
        : {};

  return {
    silent: normalized.silent === true,
    forceCustomersRefresh: normalized.forceCustomersRefresh === true,
    invalidateSlotCache: normalized.invalidateSlotCache === true,
    requireFresh: normalized.requireFresh === true,
  };
};

const mergeFetchOptions = (baseOptions = {}, nextOptions = {}) => {
  const base = normalizeFetchOptions(baseOptions);
  const next = normalizeFetchOptions(nextOptions);
  return {
    silent: base.silent && next.silent,
    forceCustomersRefresh: base.forceCustomersRefresh || next.forceCustomersRefresh,
    invalidateSlotCache: base.invalidateSlotCache || next.invalidateSlotCache,
    requireFresh: base.requireFresh || next.requireFresh,
  };
};

const doesInFlightFetchCoverOptions = (inFlightOptions = {}, nextOptions = {}) => {
  const current = normalizeFetchOptions(inFlightOptions);
  const next = normalizeFetchOptions(nextOptions);
  return (
    !next.requireFresh &&
    (current.forceCustomersRefresh || !next.forceCustomersRefresh) &&
    (current.invalidateSlotCache || !next.invalidateSlotCache)
  );
};

const hasTrustedConversationState = (slot) => {
  if (!slot || typeof slot !== 'object') return false;
  const handlingStatus = String(slot?.handling_status ?? '').trim();
  if (handlingStatus) return true;
  const attendantId =
    slot?.attendantId ??
    slot?.attendant_id ??
    slot?.attendant?.id ??
    null;
  if (attendantId !== null && attendantId !== undefined && String(attendantId).trim() !== '') {
    return true;
  }
  return slot?.in_attendance === true;
};

export const useChatClients = (attendantId, opts = {}) => {
  const {
    isAdmin = false,
    scope: scopeOption = null,
    companyId = null,
    enabled = true,
    realtimeEnabled = true,
    hasAllTeamConversationAccess = false,
    attendantTeamIds = [],
    onConversationEvent = null,
    sortMode = 'lastMessage',
  } = opts || {};
  const normalizedAttendantTeamIds = useMemo(
    () => normalizeTeamIds(attendantTeamIds),
    [attendantTeamIds]
  );
  const normalizedAttendantTeamIdsKey = useMemo(
    () => normalizedAttendantTeamIds.join(','),
    [normalizedAttendantTeamIds]
  );
  const attendantTeamIdsSet = useMemo(
    () => new Set(normalizedAttendantTeamIds),
    [normalizedAttendantTeamIds]
  );
  const normalizedCompanyId = normalizeOptionalNumber(companyId);
  const isEnabled = Boolean(enabled) && normalizedCompanyId !== null;
  const isRealtimeEnabled = isEnabled && Boolean(realtimeEnabled);
  const normalizedScope = normalizeScopeValue(scopeOption, isAdmin ? 'all' : 'attendant');
  const normalizedSortMode = normalizeConversationSortMode(sortMode);
  const shouldRestrictByTeam = !isAdmin && !hasAllTeamConversationAccess;
  const canAccessConversationByTeamScope = useCallback(
    (slot) => {
      if (!slot || typeof slot !== 'object') return false;
      if (!shouldRestrictByTeam) return true;

      const handlingStatus = String(
        slot.handling_status ?? slot.status ?? ''
      ).toLowerCase();
      const teamId = extractConversationTeamId(slot);

      if (teamId === null) {
        // Mantem o comportamento do backend: waiting_for_team sem time definido nao entra para atendente comum.
        if (handlingStatus.includes('waiting_for_team')) return false;
        return true;
      }
      return attendantTeamIdsSet.has(teamId);
    },
    [attendantTeamIdsSet, shouldRestrictByTeam]
  );
  const showAllScope = normalizedScope === 'all';
  const scopeRef = useRef(normalizedScope);
  scopeRef.current = normalizedScope;
  const scopeKeyRef = useRef('');
  const scopeKey = `${normalizedCompanyId ?? 'null'}|${normalizedScope}|${attendantId ?? 'null'}|${normalizedAttendantTeamIdsKey}|${shouldRestrictByTeam}|${normalizedSortMode}`;
  scopeKeyRef.current = scopeKey;
  const [scopeLoading, setScopeLoading] = useState(false);
  const scopeInitializedRef = useRef(false);
  const previousCompanyIdRef = useRef(normalizedCompanyId);
  // Cache em memória para clientes e contatos derivados (evita refazer flatten a cada WS tick)
  const customersCacheRef = useRef({
    customers: [],     // lista de clientes (companies)
    contacts: [],      // contactsFromCustomers derivado
    cachedAt: 0,
  });
  const clearCustomersCache = useCallback(() => {
    customersCacheRef.current = {
      customers: [],
      contacts: [],
      cachedAt: 0,
    };
  }, []);
  let initialSlotsCache;
  const getInitialSlotsCache = () => {
    if (!isEnabled) {
      return { waiting: [], attending: [], all: [] };
    }
    if (!initialSlotsCache) {
      initialSlotsCache =
        readSlotsCache(attendantId, normalizedCompanyId) ?? { waiting: [], attending: [], all: [] };
    }
    return initialSlotsCache;
  };

  const [waitingList, setWaitingList] = useState(() => getInitialSlotsCache().waiting);
  const [attendingList, setAttendingList] = useState(() => getInitialSlotsCache().attending);
  const [allList, setAllList] = useState(() => getInitialSlotsCache().all);
  const waitingListRef = useRef(waitingList);
  const attendingListRef = useRef(attendingList);
  const allListRef = useRef(allList);
  const [clientContacts, setClientContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isMounted = useRef(true);
  const loggedOutRef = useRef(false);
  const wsRef = useRef(null);
  // Coalescer de refetch para evitar tempestade de WS
  const refetchPendingRef = useRef(false);
  const fetchInFlightRef = useRef(null);
  const fetchInFlightOptionsRef = useRef(null);
  const fetchInFlightScopeRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const listRevisionRef = useRef(0);
  const recoveryTimerRef = useRef(null);
  const recoveryAttemptRef = useRef(0);
  const queuedFetchOptionsRef = useRef(null);
  const wsRefetchTimerRef = useRef(null);
  const pendingWsRefetchOptionsRef = useRef(null);
  const lastFetchCompletedAtRef = useRef(0);
  const waitingSlotIdsRef = useRef(null);
  const cancelRecovery = useCallback(() => {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = null;
  }, []);
  useEffect(() => {
    allListRef.current = allList;
  }, [allList]);

  const updateConversationLists = useCallback((waiting, attending, source) => {
    logConversationListUpdates('waitingList', waitingListRef.current, waiting, source);
    logConversationListUpdates('attendingList', attendingListRef.current, attending, source);
    waitingListRef.current = Array.isArray(waiting) ? waiting : [];
    attendingListRef.current = Array.isArray(attending) ? attending : [];
    setWaitingList(waiting);
    setAttendingList(attending);
  }, []);

  const clearScheduledWsRefetch = useCallback(() => {
    if (!wsRefetchTimerRef.current) return;
    clearTimeout(wsRefetchTimerRef.current);
    wsRefetchTimerRef.current = null;
  }, []);
  const closeConversationSubscription = useCallback(() => {
    const current = wsRef.current;
    wsRef.current = null;
    if (!current) return;
    if (typeof current === 'function') {
      try {
        current();
      } catch {
        // ignore
      }
      return;
    }
    if (
      current.readyState === WebSocket.OPEN ||
      current.readyState === WebSocket.CONNECTING
    ) {
      try {
        current.close(1000, 'cleanup');
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    const handleLogout = () => {
      loggedOutRef.current = true;
      cancelRecovery();
      fetchAbortRef.current?.abort();
      closeConversationSubscription();
      clearScheduledWsRefetch();
      pendingWsRefetchOptionsRef.current = null;
      queuedFetchOptionsRef.current = null;
      fetchInFlightRef.current = null;
      fetchInFlightOptionsRef.current = null;
      updateConversationLists([], [], 'logout');
      setAllList([]);
      setLoading(false);
      setError(null);
      waitingSlotIdsRef.current = null;
    };

    window.addEventListener(LOGOUT_EVENT, handleLogout);
    return () => {
      window.removeEventListener(LOGOUT_EVENT, handleLogout);
    };
  }, [cancelRecovery, clearScheduledWsRefetch, closeConversationSubscription, updateConversationLists]);

  useEffect(() => {
    if (isEnabled && loggedOutRef.current) {
      loggedOutRef.current = false;
    }
  }, [isEnabled, attendantId, normalizedCompanyId]);

  useEffect(() => {
    if (!isEnabled || loggedOutRef.current) {
      // encerra websocket e timers quando estiver desabilitado (logout ou falta de company/token)
      cancelRecovery();
      fetchAbortRef.current?.abort();
      closeConversationSubscription();
      clearScheduledWsRefetch();
      pendingWsRefetchOptionsRef.current = null;
      setLoading(false);
      if (loggedOutRef.current) {
        updateConversationLists([], [], 'disabled_logout');
        setAllList([]);
        setError(null);
        waitingSlotIdsRef.current = null;
      }
      return;
    }

    const cached = readSlotsCache(attendantId, normalizedCompanyId);
    if (cached) {
      updateConversationLists(cached.waiting, cached.attending, 'cache_restore');
      setAllList(cached.all);
    }
  }, [
    attendantId,
    normalizedCompanyId,
    isEnabled,
    cancelRecovery,
    clearScheduledWsRefetch,
    closeConversationSubscription,
    updateConversationLists,
  ]);

  useEffect(() => {
    const previousCompanyId = previousCompanyIdRef.current;
    const hasRealCompanySwitch =
      previousCompanyId !== null &&
      normalizedCompanyId !== null &&
      previousCompanyId !== normalizedCompanyId;

    if (hasRealCompanySwitch) {
      cancelRecovery();
      recoveryAttemptRef.current = 0;
      fetchAbortRef.current?.abort();
      clearCustomersCache();
      updateConversationLists([], [], 'company_switch');
      setAllList([]);
      setError(null);
      waitingSlotIdsRef.current = null;
    }

    if (normalizedCompanyId !== null) {
      previousCompanyIdRef.current = normalizedCompanyId;
    }
  }, [cancelRecovery, clearCustomersCache, normalizedCompanyId, updateConversationLists]);

  /* Formatador de telefone */
  const formatPhoneNumber = (rawNumber) => formatBrazilianPhone(rawNumber);

  const buildSlotObjects = useCallback(
    (slots, contacts, customers) =>
      slots.map((slot) => {
        const rawWhatsapp = extractSlotRawWhatsapp(slot);
        const conversationId = extractSlotConversationId(slot);

        const clientContact = contacts.find((c) =>
          arePhoneDigitsEquivalent(
            c?.whatsapp_number ??
              c?.contact_phone ??
              c?.phone_number ??
              c?.phone ??
              c?.whatsapp_jid ??
              c?.whatsapp_lid,
            rawWhatsapp
          )
        );

        const clientId = slot.client_id || slot.customer_id || clientContact?.client_id || null;
        const client = customers.find((c) => c.id === clientId) || null;
        const slotCompanyId = slot.company_id ?? slot.companyId ?? null;
        const slotWorkflowState =
          Object.prototype.hasOwnProperty.call(slot, 'workflow_state')
            ? slot.workflow_state
            : Object.prototype.hasOwnProperty.call(slot, 'workflowState')
              ? slot.workflowState
              : undefined;

        const lastMessage = slot.messages?.[slot.messages.length - 1] ?? null;
        const firstMessage = Array.isArray(slot.messages) && slot.messages.length > 0 ? slot.messages[0] : null;

        // Smart display name: tenta diversas fontes (contato, push/profile, cliente)
        const normalizeDigits = (n) => String(n || '').replace(/\D/g, '');
        const sanitize = (v) => {
          if (!v && v !== 0) return null;
          const s = String(v).trim();
          return s || null;
        };
        const digits = normalizeDigits(rawWhatsapp);

        const whatsappCandidates = [
          slot.push_name,
          slot.profile_name,
          slot.display_name,
          lastMessage?.push_name,
          lastMessage?.sender_name,
          lastMessage?.profile_name,
        ].map(sanitize);

        let resolvedName = whatsappCandidates.find((n) => n && normalizeDigits(n) !== digits) || null;

        if (!resolvedName) {
          const rawCandidates = [
            clientContact?.name,
            client?.contact_name,
            client?.name,
            slot.name,
            slot.customer_name,
          ].map(sanitize);
          resolvedName = rawCandidates.find((n) => n && normalizeDigits(n) !== digits) || null;
        }

        const slotContacts = Array.isArray(slot?.contacts)
          ? slot.contacts.filter(Boolean).map((contact) => ({ ...contact }))
          : [];
        const slotContact =
          slot?.contact && typeof slot.contact === 'object'
            ? { ...slot.contact }
            : null;

        const resolvedClientContact =
          slotContacts.length > 0
            ? slotContacts[0]
            : slotContact ||
              clientContact ||
              null;

        const resolvedPhoto =
          resolvedClientContact?.photograph ||
          resolvedClientContact?.photo ||
          slotContact?.photograph ||
          slotContact?.photo ||
          slot.contact_photo ||
          slot.customer_photo ||
          slot.client_photo ||
          slot.photo ||
          slot.photograph ||
          client?.photograph ||
          null;

        const resolvedTag = resolveCustomerTag(slot, client);

        const normalizedAttendantId =
          slot.attendant_id === null || slot.attendant_id === undefined
            ? null
            : Number(slot.attendant_id);

        // Detecta quem iniciou a conversa
        const guessInitiator = () => {
          if (firstMessage) {
            const authorType = normalizeAuthorType(firstMessage);
            if (authorType) {
              return authorType === 'client' ? 'client' : 'attendant';
            }
            const s = (firstMessage?.sender || '').toString().toLowerCase();
            // Heurística: se parecer enviado por atendente/bot, consideramos atendente
            const byAttendant = /attendant|atendente|agent|operador|bot/.test(s);
            return byAttendant ? 'attendant' : 'client';
          }
          // Sem mensagens: pode ser slot aberto pelo atendente
          return 'unknown';
        };

        const aggregatedContacts =
          slotContacts.length > 0
            ? slotContacts
            : resolvedClientContact
              ? [{ ...resolvedClientContact }]
              : [];

        const queueSource = (() => {
          // Marca como "fila de dispositivo" somente quando a primeira mensagem identificável vem do cliente
          // (WhatsApp/inbound). Slots iniciados por atendente/bot ou sem mensagens não entram nesse filtro.
          const firstRelevant =
            (Array.isArray(slot.messages) ? slot.messages.find((m) => m && m.author_type !== 'system') : null) ||
            firstMessage;
          if (!firstRelevant) return null;
          const initiator = guessInitiator();
          const author = normalizeAuthorType(firstRelevant);
          const senderLooksAttendant =
            isAttendantMessage(firstRelevant) ||
            isBotMessage(firstRelevant) ||
            isSystemMessage(firstRelevant);
          const authorLooksAttendant = Boolean(author && author !== 'client');
          const fromClient = author === 'client' || (!senderLooksAttendant && !authorLooksAttendant);
          if (initiator === 'attendant') return null;
          if (initiator === 'client' || fromClient) return 'device';
          return null;
        })();

        const base = {
          id: conversationId,
          protocol: slot.protocol ?? null,
          clientId,
          client_name: client?.name || null,
          in_attendance: slot.in_attendance,
          session_id: slot.session_id ?? slot.sessionId ?? null,
          sessionId: slot.session_id ?? slot.sessionId ?? null,
          whatsapp_session_id:
            slot.whatsapp_session_id ??
            slot.whatsappSessionId ??
            slot.session_id ??
            slot.sessionId ??
            null,
          whatsappSessionId:
            slot.whatsapp_session_id ??
            slot.whatsappSessionId ??
            slot.session_id ??
            slot.sessionId ??
            null,
          whatsapp_connection_id:
            slot.whatsapp_connection_id ?? slot.whatsappConnectionId ?? null,
          whatsappConnectionId:
            slot.whatsapp_connection_id ?? slot.whatsappConnectionId ?? null,
          whatsapp_connection_name:
            slot.whatsapp_connection_name ??
            slot.whatsapp_session_display_name ??
            slot.whatsappConnectionName ??
            slot.whatsappSessionDisplayName ??
            null,
          whatsappConnectionName:
            slot.whatsapp_connection_name ??
            slot.whatsapp_session_display_name ??
            slot.whatsappConnectionName ??
            slot.whatsappSessionDisplayName ??
            null,
          whatsapp_session_display_name:
            slot.whatsapp_session_display_name ??
            slot.whatsapp_connection_name ??
            slot.whatsappSessionDisplayName ??
            slot.whatsappConnectionName ??
            null,
          company_whatsapp_sessions_count:
            slot.company_whatsapp_sessions_count ??
            slot.whatsapp_connections_count ??
            slot.whatsappConnectionsCount ??
            0,
          company_id: slotCompanyId,
          companyId: slotCompanyId,
          closed_at: slot.closed_at,
          evaluation: slot.evaluation,
          team_id: slot.team_id,
          handling_status: slot.handling_status,
          created_at: slot.created_at,
          updated_at: slot.updated_at,
          rawWhatsappNumber: rawWhatsapp,
          whatsappNumber: formatPhoneNumber(rawWhatsapp),
          messages: slot.messages,
          comments: Array.isArray(slot.comments) ? slot.comments : [],
          isReserved: slot.is_reserved,
          status: slot.status,
          workflow_state: slotWorkflowState,
          workflowState: slotWorkflowState,
          name: resolvedName || null,
          photo: resolvedPhoto,
          contact: resolvedClientContact
            ? { ...resolvedClientContact }
            : slotContact
              ? { ...slotContact }
              : null,
          contacts: aggregatedContacts,
          attendantId: Number.isNaN(normalizedAttendantId)
            ? slot.attendant_id
            : normalizedAttendantId,
          attendantName: slot.attendant_name || null,
          transfer_request_status: slot.transfer_request_status ?? null,
          transfer_request_summary: slot.transfer_request_summary ?? null,
          transfer_request_from_attendant_id:
            slot.transfer_request_from_attendant_id ?? null,
          transfer_request_from_attendant_name:
            slot.transfer_request_from_attendant_name ?? null,
          transfer_request_to_attendant_id:
            slot.transfer_request_to_attendant_id ?? null,
          transfer_request_to_attendant_name:
            slot.transfer_request_to_attendant_name ?? null,
          transfer_request_created_at: slot.transfer_request_created_at ?? null,
          transfer_request_responded_at:
            slot.transfer_request_responded_at ?? null,
          last_message_at: slot.last_message_at ?? slot.lastMessageAt ?? null,
          lastMessageAt: slot.lastMessageAt ?? slot.last_message_at ?? null,
          waiting_since: slot.waiting_since ?? slot.waitingSince ?? null,
          waitingSince: slot.waitingSince ?? slot.waiting_since ?? null,
          attendance_started_at:
            slot.attendance_started_at ?? slot.attendanceStartedAt ?? null,
          attendanceStartedAt:
            slot.attendanceStartedAt ?? slot.attendance_started_at ?? null,
          lastMessageSender: lastMessage?.sender || null,
          initiatedBy: guessInitiator(),
          isOutboundInitiated: false,
          queueSource,
          isDeviceQueue: queueSource === 'device',
          tag: resolvedTag,
        };
        return base;
      }),
    []
  );

  // Garante que slots sem company_id explícito herdem o company_id atual (evita descarte na filtragem)
  const applyCompanyFallback = useCallback(
    (slots) => {
      const list = Array.isArray(slots) ? slots.filter(Boolean) : [];
      if (normalizedCompanyId === null) return list;
      return list.map((slot) => {
        const companyId = getConversationCompanyId(slot);
        if (companyId !== null) return slot;
        return {
          ...slot,
          company_id: normalizedCompanyId,
          companyId: normalizedCompanyId,
        };
      });
    },
    [normalizedCompanyId]
  );

  // Carrega apenas slots e clientes, NÃO contatos!
  const fetchConversationsPage = useCallback(
    async (options = {}) => {
      const normalizedOptions = normalizeFetchOptions(options);
      const {
        silent,
        forceCustomersRefresh,
        invalidateSlotCache,
      } = normalizedOptions;

      if (!isMounted.current) {
        return { waiting: [], attending: [], all: [] };
      }

      if (!isEnabled) {
        if (isMounted.current && !silent) setLoading(false);
        return { waiting: [], attending: [], all: [] };
      }

      if (fetchInFlightRef.current) {
        if (
          fetchInFlightScopeRef.current !== scopeKey ||
          !doesInFlightFetchCoverOptions(fetchInFlightOptionsRef.current, normalizedOptions)
        ) {
          queuedFetchOptionsRef.current = mergeFetchOptions(
            queuedFetchOptionsRef.current ?? { silent: true },
            normalizedOptions
          );
        }
        return fetchInFlightRef.current;
      }

      cancelRecovery();
      const controller = new AbortController();
      fetchAbortRef.current = controller;
      const fetchScopeKey = scopeKey;
      const fetchRevision = listRevisionRef.current;
      const isScopeCurrent = () =>
        isMounted.current && !loggedOutRef.current && scopeKeyRef.current === fetchScopeKey;
      const currentLists = () => ({
        waiting: waitingListRef.current,
        attending: attendingListRef.current,
        all: allListRef.current,
      });
      const request = Promise.resolve().then(async () => {
        try {
          if (!isScopeCurrent() || controller.signal.aborted) return currentLists();
          if (invalidateSlotCache) {
            try {
              clearChatSlotsCache();
            } catch (err) {
              logSafeWarning('Falha ao limpar cache de slots antes do refetch.', err);
            }
            clearCustomersCache();
          }

          if (!silent) setLoading(true);
          const effectiveScope = scopeRef.current || normalizedScope;
          const showAllScopeForFetch = effectiveScope === 'all';
          // 1) slots sempre atualizam; já buscamos a fila dedicada em paralelo
          const [slots, waitingRaw] = await fetchChatListPair(
            getActiveConversations({
              scope: effectiveScope,
              company_id: normalizedCompanyId,
              attendant_id: showAllScopeForFetch ? null : attendantId,
              hasAllTeamConversationAccess,
              sort_mode: normalizedSortMode,
            }, {
              fetchAll: false,
              signal: controller.signal,
              timeout: CHAT_LIST_REQUEST_TIMEOUT_MS,
            }),
            getConversationsInWaiting(normalizedCompanyId, {
              scope: effectiveScope,
              hasAllTeamConversationAccess,
              fetchAll: false,
              sort_mode: normalizedSortMode,
              signal: controller.signal,
              timeout: CHAT_LIST_REQUEST_TIMEOUT_MS,
            }),
          );
          if (!isScopeCurrent()) {
            return currentLists();
          }
          if (fetchRevision !== listRevisionRef.current) {
            // A realtime event is newer than this HTTP snapshot. Reconcile once,
            // after the existing cooldown, instead of overwriting the event.
            queuedFetchOptionsRef.current = mergeFetchOptions(
              queuedFetchOptionsRef.current ?? { silent: true },
              { silent: true, requireFresh: true }
            );
            return currentLists();
          }
          // 2) contatos: as listas de conversa já trazem o contato resumido.
          // Evita varrer todos os contatos da empresa em cada carga do chat.
          let customersList = customersCacheRef.current.customers;
          let contactsFromCustomers = customersCacheRef.current.contacts;
          const shouldRefreshCustomers =
            forceCustomersRefresh ||
            !silent ||
              !Array.isArray(contactsFromCustomers) ||
              contactsFromCustomers.length === 0;

          if (shouldRefreshCustomers) {
            customersList = [];
            contactsFromCustomers = [];
            customersCacheRef.current = {
              customers: customersList,
              contacts: contactsFromCustomers,
              cachedAt: Date.now(),
            };
          }
          // NÃO recarregue todos os contatos aqui em toda atualização silenciosa

          let formattedSlots = applyCompanyFallback(buildSlotObjects(slots, contactsFromCustomers, customersList));
          let waitingIdsForLists = new Set();
          let normalizedWaiting = [];
          let nextWaitingSlotIds = new Set();
          if (normalizedCompanyId !== null) {
            formattedSlots = formattedSlots.filter(
              (slot) => getConversationCompanyId(slot) === normalizedCompanyId
            );
          } else {
            formattedSlots = formattedSlots.filter(Boolean);
          }
          formattedSlots = formattedSlots.filter(canAccessConversationByTeamScope);

          if (Array.isArray(waitingRaw)) {
            normalizedWaiting = applyCompanyFallback(
              buildSlotObjects(waitingRaw, contactsFromCustomers, customersList)
            );
            if (normalizedCompanyId !== null) {
              normalizedWaiting = normalizedWaiting.filter(
                (slot) => getConversationCompanyId(slot) === normalizedCompanyId
              );
            } else {
              normalizedWaiting = normalizedWaiting.filter(Boolean);
            }
            normalizedWaiting = normalizedWaiting.filter(canAccessConversationByTeamScope);
            waitingIdsForLists = new Set(
              normalizedWaiting
                .map((slot) => (slot && slot.id !== null && slot.id !== undefined ? String(slot.id) : null))
                .filter(Boolean)
            );
            nextWaitingSlotIds = waitingIdsForLists;

            if (normalizedWaiting.length > 0) {
              const byId = new Map(formattedSlots.map((slot) => [slot.id, slot]));

              normalizedWaiting.forEach((waitingSlot) => {
                if (!waitingSlot || waitingSlot.id === null || waitingSlot.id === undefined) return;
                const existing = byId.get(waitingSlot.id);
                const existingIsAttending =
                  existing?.in_attendance === true ||
                  String(existing?.handling_status || '').toLowerCase().includes('attendant_handling');
                const preparedWaiting = {
                  ...waitingSlot,
                  in_attendance: false,
                  mergedSlotsHasWaiting: true,
                  handling_status: waitingSlot.handling_status || 'waiting_attendant',
                };
                // Não sobrescrever um slot que já está em atendimento
                if (byId.has(waitingSlot.id) && !existingIsAttending) {
                  byId.set(waitingSlot.id, {
                    ...existing,
                    ...preparedWaiting,
                    in_attendance: false,
                    mergedSlotsHasWaiting: true,
                    handling_status: preparedWaiting.handling_status || existing.handling_status,
                  });
                } else {
                  byId.set(waitingSlot.id, preparedWaiting);
                }
              });

              formattedSlots = Array.from(byId.values());
            }
          }

          // Usa o mapa de inícios outbound recentes para refinar o initiatedBy
          try {
            const outbound = readOutboundInitMap();
            if (outbound && Object.keys(outbound).length > 0) {
              const now = Date.now();
              formattedSlots = formattedSlots.map((s) => {
                if (!s || !s.rawWhatsappNumber) return s;
                const ts = outbound[s.rawWhatsappNumber];
                if (!ts) return s;
                // Usa a marca local apenas como dica temporária, sem sobrescrever
                // estados já confirmados pelo backend (handling/attendant/attendance).
                if (now - Number(ts) <= OUTBOUND_INIT_TTL_MS && !hasTrustedConversationState(s)) {
                  return { ...s, initiatedBy: 'attendant', isOutboundInitiated: true, queueSource: null, isDeviceQueue: false };
                }
                return s;
              });
            }
          } catch {
            // Se a leitura do cache local falhar, seguimos com a lista atual.
          }

          const lists = computeConversationListsFromMerged(
            mergeConversationsByClient(formattedSlots),
            attendantId,
            normalizedCompanyId,
            showAllScopeForFetch,
            waitingIdsForLists,
            normalizedSortMode
          );
          const { waiting, attending, all } = lists;

          if (!isScopeCurrent()) {
            return { waiting: [], attending: [], all: [] };
          }

          waitingSlotIdsRef.current = nextWaitingSlotIds;
          recoveryAttemptRef.current = 0;
          writeSlotsCache(attendantId, normalizedCompanyId, { waiting, attending, all });

          if (isMounted.current) {
            updateConversationLists(waiting, attending, 'fetch_hydration');
            setAllList(all);
            setError(null);
          }
          debugNotificationFlow('conversation_refetch_hydrated', {
            source: 'fetchConversationsPage',
            waiting_count: waiting.length,
            attending_count: attending.length,
            all_count: all.length,
            silent,
            invalidateSlotCache,
            forceCustomersRefresh,
          });
          lastFetchCompletedAtRef.current = Date.now();

          return { waiting, attending, all };
        } catch (err) {
          if (!isScopeCurrent() || controller.signal.aborted) return currentLists();
          logSafeError('Erro ao buscar slots/clientes.', err);
          const status = Number(err?.response?.status || 0);
          if (status === 401 || status === 403) {
            updateConversationLists([], [], 'access_denied');
            setAllList([]);
            waitingSlotIdsRef.current = null;
            queuedFetchOptionsRef.current = null;
            clearScheduledWsRefetch();
            pendingWsRefetchOptionsRef.current = null;
          }
          if (status === 401) {
            emitLogoutEvent();
            loggedOutRef.current = true;
          }
          setError(err);
          if (
            isRetryableChatListError(err) &&
            recoveryAttemptRef.current < CHAT_LIST_RETRY_LIMIT &&
            document.visibilityState !== 'hidden' && navigator.onLine !== false
          ) {
            const delay = chatListRetryDelay(err, recoveryAttemptRef.current++);
            recoveryTimerRef.current = setTimeout(() => {
              recoveryTimerRef.current = null;
              if (isScopeCurrent() && document.visibilityState !== 'hidden' && navigator.onLine !== false) {
                fetchConversationsPageRef.current?.({ silent: true });
              }
            }, delay);
          }
          return currentLists();
        } finally {
          if (fetchInFlightRef.current === request) {
            fetchInFlightRef.current = null;
            fetchInFlightOptionsRef.current = null;
            fetchInFlightScopeRef.current = null;
            fetchAbortRef.current = null;
            lastFetchCompletedAtRef.current = Date.now();
            if (isMounted.current && !silent) setLoading(false);
            const queuedOptions = queuedFetchOptionsRef.current;
            queuedFetchOptionsRef.current = null;
            if (queuedOptions && isMounted.current && !loggedOutRef.current && !recoveryTimerRef.current) {
              scheduleWsRefetchRef.current?.(queuedOptions);
            }
          }
        }
      });

      fetchInFlightRef.current = request;
      fetchInFlightOptionsRef.current = normalizedOptions;
      fetchInFlightScopeRef.current = fetchScopeKey;
      return request;
    },
    [
      applyCompanyFallback,
      buildSlotObjects,
      canAccessConversationByTeamScope,
      attendantId,
      normalizedScope,
      normalizedCompanyId,
      isEnabled,
      hasAllTeamConversationAccess,
      normalizedSortMode,
      clearCustomersCache,
      updateConversationLists,
      cancelRecovery,
      clearScheduledWsRefetch,
      scopeKey,
    ]
  );

  const scheduleWsRefetch = useCallback(
    (options = {}) => {
      if (!isEnabled || loggedOutRef.current) return;
      if (recoveryTimerRef.current) return;
      const immediate = options?.immediate === true;

      pendingWsRefetchOptionsRef.current = mergeFetchOptions(
        pendingWsRefetchOptionsRef.current ?? { silent: true },
        options
      );

      if (wsRefetchTimerRef.current) {
        if (!immediate) return;
        clearTimeout(wsRefetchTimerRef.current);
        wsRefetchTimerRef.current = null;
      }

      const elapsedSinceLastFetch = Date.now() - lastFetchCompletedAtRef.current;
      const delay =
        immediate
          ? 0
          : lastFetchCompletedAtRef.current > 0
          ? Math.max(0, WS_REFETCH_COOLDOWN_MS - elapsedSinceLastFetch)
          : 0;

      wsRefetchTimerRef.current = setTimeout(() => {
        wsRefetchTimerRef.current = null;
        const nextOptions = pendingWsRefetchOptionsRef.current ?? { silent: true };
        pendingWsRefetchOptionsRef.current = null;
        fetchConversationsPage(nextOptions)
          .catch((err) => {
            logSafeError('Erro ao atualizar slots via WS.', err);
          })
          .finally(() => {
            if (pendingWsRefetchOptionsRef.current) {
              scheduleWsRefetch({ silent: true });
            }
          });
      }, delay);
    },
    [fetchConversationsPage, isEnabled]
  );

  // Função para buscar contatos de um cliente sob demanda
  const fetchClientContacts = useCallback(async (clientId) => {
    if (!clientId) return [];
    try {
      setLoading(true);

      try {
        const cachedList = readContactsCacheList();
        if (Array.isArray(cachedList) && cachedList.length > 0) {
          if (isMounted.current) setClientContacts(cachedList);
          return cachedList;
        }
      } catch {
        // ignora falhas de leitura do cache
      }

      if (isMounted.current) setClientContacts([]);
      return [];
    } catch (err) {
      if (isMounted.current) setError(err);
      return [];
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  // Reaplica o escopo (admin vs. atendente) preservando o último snapshot até o novo fetch concluir
  useEffect(() => {
    if (!isEnabled) {
      setScopeLoading(false);
      return;
    }
    if (!scopeInitializedRef.current) {
      scopeInitializedRef.current = true;
      setScopeLoading(false);
      return;
    }
    setScopeLoading(true);
    fetchConversationsPage({ silent: true, invalidateSlotCache: true })
      .catch(() => {})
      .finally(() => {
        if (isMounted.current) setScopeLoading(false);
      });
  }, [normalizedScope, isEnabled, fetchConversationsPage]);

  const handleIncomingPayload = useCallback(
    (payload, options = {}) => {
      if (!payload || typeof payload !== 'object') return;
      if (!isEnabled || loggedOutRef.current) return;
      if (scopeKeyRef.current !== scopeKey) return;

      const { fallbackToRefetch = false, suppressLocalEvent = false } = options || {};

      let handledIncremental = false;
      let forceRefetch = false;
      let shouldRefetch = false;

      const rawType = payload?.type;
      const rawEvent = payload?.event;
      const normalizedType = normalizeWsToken(rawType);
      const normalizedEvent = normalizeWsToken(rawEvent);
      const shouldInvalidateCustomersCache =
        CUSTOMERS_CACHE_INVALIDATION_WS_TYPES.has(normalizedType) ||
        CUSTOMERS_CACHE_INVALIDATION_WS_TYPES.has(normalizedEvent);
      const incomingSlot = payload?.slot || payload?.conversation || null;
      const incomingMessage = withLocalTimestampFallback(extractPayloadMessage(payload));
      const realtimeTrace = buildRealtimeNotificationTrace(
        payload,
        normalizedType,
        normalizedEvent,
        incomingSlot,
        incomingMessage
      );
      debugNotificationFlow('realtime_event_received', realtimeTrace);
      const hasIncomingSlot = Boolean(incomingSlot && typeof incomingSlot === 'object');
      const incomingMessageConversationId = extractMessageConversationId(incomingMessage);
      const messageTargetSlot = (allListRef.current || []).find((slot) =>
        doesSlotContainConversationId(slot, incomingMessageConversationId)
      );
      const hasKnownMessageTarget = Boolean(messageTargetSlot);
      const hasMessageData = Boolean(incomingMessage && incomingMessageConversationId !== null && incomingMessageConversationId !== undefined);
      const hasSufficientPayload = hasIncomingSlot || (hasMessageData && hasKnownMessageTarget);
      const shouldRefetchEvaluationWorkflow = Boolean(
        hasMessageData &&
          messageTargetSlot &&
          isClientMessage(incomingMessage) &&
          isConversationEvaluationActive(messageTargetSlot)
      );
      const isStatusOnlyMessageEvent = hasMessageStatusSignal(payload, normalizedType, normalizedEvent);
      const hasCompositionHint = hasCompositionRefreshHint(payload, normalizedType, normalizedEvent);
      const rawText =
        normalizedType === 'raw_text_message' && typeof payload?.raw === 'string'
          ? payload.raw.toLowerCase()
          : '';

      if (normalizedType.startsWith('group_') || normalizedEvent.startsWith('group_')) {
        return;
      }

      if (NON_SLOT_WS_TYPES.has(normalizedType) || NON_SLOT_WS_TYPES.has(normalizedEvent)) {
        return;
      }

      if (shouldInvalidateCustomersCache) {
        clearCustomersCache();
      }

      forceRefetch =
        payload?.force_refetch === true ||
        payload?.forceRefetch === true ||
        payload?.refetch === true ||
        payload?.refresh === true;
      const hasConversationBatch = Array.isArray(payload?.conversations);
      const batchEvent =
        hasConversationBatch ||
        normalizedType === 'chats_synced' ||
        normalizedEvent === 'chats_synced';
      shouldRefetch =
        forceRefetch ||
        batchEvent ||
        shouldRefetchEvaluationWorkflow ||
        (hasCompositionHint && !hasSufficientPayload) ||
        (payload?.is_new_message === true && !hasSufficientPayload);
      if (!shouldRefetch && rawText) {
        const queueHint =
          rawText.includes(' na fila') ||
          /\bfila\b/.test(rawText) ||
          rawText.includes('waiting') ||
          rawText.includes('queue');
        if (queueHint) {
          shouldRefetch = true;
        }
      }

      const hasConversationData = Boolean(
        payload?.slot || payload?.conversation || payload?.message || payload?.messages
      );
      if (
        (normalizedType.startsWith('conversation') || normalizedEvent.startsWith('conversation')) &&
        !hasConversationData &&
        (hasCompositionHint || !isStatusOnlyMessageEvent)
      ) {
        shouldRefetch = true;
      }

      if (
        (!batchEvent && hasConversationData) ||
        normalizedType.startsWith('slot_') ||
        (!batchEvent && normalizedType.startsWith('conversation')) ||
        (!batchEvent && normalizedEvent.startsWith('conversation'))
      ) {
        if (incomingSlot && typeof incomingSlot === 'object') {
          try {
            if (shouldRestrictByTeam && !hasExplicitTeamMetadata(incomingSlot)) {
              handledIncremental = true;
              shouldRefetch = true;
            } else {
              const customers = customersCacheRef.current.customers || [];
              const contacts = customersCacheRef.current.contacts || [];
              const normalized = applyCompanyFallback(
                buildSlotObjects([incomingSlot], contacts, customers)
              )[0];
              if (normalized) {
                const slotCompanyId = getConversationCompanyId(normalized);
                if (
                  normalizedCompanyId !== null &&
                  slotCompanyId !== normalizedCompanyId
                ) {
                  handledIncremental = true;
                } else if (!canAccessConversationByTeamScope(normalized)) {
                  handledIncremental = true;
                  shouldRefetch = true;
                } else {
                  if (waitingSlotIdsRef.current !== null) {
                    const waitingSet =
                      waitingSlotIdsRef.current instanceof Set
                        ? new Set(
                            [...waitingSlotIdsRef.current]
                              .map((id) =>
                                id === null || id === undefined ? null : String(id)
                              )
                              .filter(Boolean)
                          )
                        : new Set();
                    const normalizedId = normalized?.id;
                    const isWaitingSlot =
                      isConversationOpen(normalized) &&
                      !isConversationAttending(normalized) &&
                      !isConversationAwaitingEvaluation(normalized) &&
                      isWaitingRouteStatus(normalized);
                    if (normalizedId !== null && normalizedId !== undefined) {
                      if (isWaitingSlot) {
                        waitingSet.add(String(normalizedId));
                        logCompositionListMove('added_to_waiting', normalized);
                      } else {
                        const removed = waitingSet.delete(String(normalizedId));
                        if (removed && isConversationAttending(normalized)) {
                          logCompositionListMove('removed_from_waiting_by_assign', normalized);
                        }
                      }
                      waitingSlotIdsRef.current = waitingSet;
                    }
                  }

                  // Atualiza o estado combinando com o agregado existente
                  setAllList((prev) => {
                    const prevMergedBase = Array.isArray(prev) ? prev.slice() : [];
                    const scopedPrevMerged =
                      normalizedCompanyId === null
                        ? prevMergedBase.filter(Boolean)
                        : prevMergedBase.filter(
                            (slotItem) => getConversationCompanyId(slotItem) === normalizedCompanyId
                          );
                    const key = getConversationGroupKey(normalized);
                    const idx = scopedPrevMerged.findIndex(
                      (s) => s?.mergedSlotsKey === key || getConversationGroupKey(s) === key
                    );
                    const wasAttending =
                      idx !== -1 ? isConversationAttending(scopedPrevMerged[idx]) : false;

                    if (idx !== -1) {
                      scopedPrevMerged[idx] = mergeAggregatedWithConversation(
                        scopedPrevMerged[idx],
                        normalized
                      );
                    } else {
                      const newAgg = {
                        ...normalized,
                        mergedSlotsKey: getConversationGroupKey(normalized),
                        mergedSlotIds: [normalized.id],
                        mergedSlotsCount: 1,
                        mergedSlotsHasWaiting:
                          !isConversationAttending(normalized) &&
                          !isConversationAwaitingEvaluation(normalized),
                        mergedSlotsHasAttending: isConversationAttending(normalized),
                        mergedSlotsAttendantIds: normalized.attendantId
                          ? [normalized.attendantId]
                          : [],
                      };
                      scopedPrevMerged.push(newAgg);
                    }

                    const teamScopedMerged = scopedPrevMerged.filter(canAccessConversationByTeamScope);

                    // recomputa waiting/attending/all a partir do merged
                    const lists = computeConversationListsFromMerged(
                      teamScopedMerged,
                      attendantId,
                      normalizedCompanyId,
                      showAllScope,
                      waitingSlotIdsRef.current,
                      normalizedSortMode
                    );
                    if (isMounted.current) {
                      updateConversationLists(
                        lists.waiting,
                        lists.attending,
                        'realtime_slot_incremental'
                      );
                    }
                    if (!wasAttending && isConversationAttending(normalized)) {
                      logCompositionListMove('added_to_active', normalized);
                    }

                    // Retorna o novo allList (ordenado)
                    return lists.all;
                  });

                  handledIncremental = true;
                }
              }
            }
          } catch (err) {
            logSafeError('Falha ao aplicar update incremental do slot via WS.', err);
            handledIncremental = true;
          }
        } else if (hasMessageData && hasKnownMessageTarget) {
          try {
            const messageConversationId = incomingMessageConversationId;
            setAllList((prev) => {
              const prevMergedBase = Array.isArray(prev) ? prev.slice() : [];
              let changed = false;
              const nextMerged = prevMergedBase.map((slot) => {
                if (!doesSlotContainConversationId(slot, messageConversationId)) return slot;
                changed = true;
                return mergeAggregatedWithConversation(slot, {
                  ...slot,
                  id: messageConversationId,
                  messages: [incomingMessage],
                  updated_at:
                    incomingMessage?.timestamp ??
                    incomingMessage?.updated_at ??
                    slot?.updated_at,
                });
              });

              if (!changed) return prev;

              const lists = computeConversationListsFromMerged(
                nextMerged.filter(canAccessConversationByTeamScope),
                attendantId,
                normalizedCompanyId,
                showAllScope,
                waitingSlotIdsRef.current,
                normalizedSortMode
              );
              if (isMounted.current) {
                updateConversationLists(
                  lists.waiting,
                  lists.attending,
                  'realtime_message_incremental'
                );
              }
              return lists.all;
            });
            handledIncremental = true;
            shouldRefetch = false;
          } catch (err) {
            logSafeError('Falha ao aplicar update incremental da mensagem via WS.', err);
            handledIncremental = true;
          }
        } else {
          handledIncremental = true;
          if (
            fallbackToRefetch &&
            (payload?.message || payload?.messages) &&
            !isStatusOnlyMessageEvent
          ) {
            shouldRefetch = true;
          }
        }
      } else if (!forceRefetch) {
        handledIncremental = true;
      }

      if (!suppressLocalEvent) {
        try {
          window.dispatchEvent(
            new CustomEvent('bluezap:conversation-event', {
              detail: {
                ...payload,
                [REALTIME_LIST_SYNC_META_KEY]: {
                  owner: 'useChatClients',
                  handledIncremental,
                  requiresFullRefetch: shouldRefetch,
                  fullRefetchAlreadyPending: shouldRefetch && refetchPendingRef.current,
                  eventType: normalizedType || normalizedEvent || '',
                },
              },
            })
          );
        } catch {
          // Falha ao propagar evento local nao deve interromper o refetch.
        }
      }

      // Se não tratamos incrementalmente, faz o refetch completo (fallback)
      if (handledIncremental) listRevisionRef.current += 1;
      if (!handledIncremental && !shouldRefetch) return;
      if (!shouldRefetch) return;
      if (refetchPendingRef.current) return;
      debugNotificationFlow('realtime_refetch_requested', {
        ...realtimeTrace,
        forceRefetch,
        shouldRefetchEvaluationWorkflow,
        hasCompositionHint,
        hasSufficientPayload,
        isStatusOnlyMessageEvent,
      });
      refetchPendingRef.current = true;
      scheduleWsRefetch({
        silent: true,
        forceCustomersRefresh: shouldInvalidateCustomersCache,
        invalidateSlotCache: shouldInvalidateCustomersCache || shouldRefetchEvaluationWorkflow,
        immediate: shouldRefetchEvaluationWorkflow,
        requireFresh: true,
      });
      window.setTimeout(() => {
        refetchPendingRef.current = false;
      }, WS_REFETCH_COOLDOWN_MS);
    },
    [
      applyCompanyFallback,
      attendantId,
      buildSlotObjects,
      canAccessConversationByTeamScope,
      clearCustomersCache,
      isEnabled,
      normalizedCompanyId,
      scheduleWsRefetch,
      scopeKey,
      showAllScope,
      shouldRestrictByTeam,
      normalizedSortMode,
      updateConversationLists,
    ]
  );

  const fetchConversationsPageRef = useRef(fetchConversationsPage);
  const handleIncomingPayloadRef = useRef(handleIncomingPayload);
  const onConversationEventRef = useRef(onConversationEvent);
  const scheduleWsRefetchRef = useRef(scheduleWsRefetch);
  const clearScheduledWsRefetchRef = useRef(clearScheduledWsRefetch);
  const closeConversationSubscriptionRef = useRef(closeConversationSubscription);

  fetchConversationsPageRef.current = fetchConversationsPage;
  handleIncomingPayloadRef.current = handleIncomingPayload;
  onConversationEventRef.current = onConversationEvent;
  scheduleWsRefetchRef.current = scheduleWsRefetch;
  clearScheduledWsRefetchRef.current = clearScheduledWsRefetch;
  closeConversationSubscriptionRef.current = closeConversationSubscription;

  useEffect(() => {
    const handleOnline = () => scheduleWsRefetchRef.current?.({ silent: true, requireFresh: true });
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // Efeito para carregar os slots e configurar o websocket
  useEffect(() => {
    if (!isEnabled || loggedOutRef.current) {
      isMounted.current = false;
      closeConversationSubscriptionRef.current?.();
      return () => {
        closeConversationSubscriptionRef.current?.();
      };
    }

    isMounted.current = true;
    if (!isRealtimeEnabled) {
      closeConversationSubscriptionRef.current?.();
      return () => {
        isMounted.current = false;
        cancelRecovery();
        fetchAbortRef.current?.abort();
        clearScheduledWsRefetchRef.current?.();
        closeConversationSubscriptionRef.current?.();
      };
    }

    fetchConversationsPageRef.current?.();
    let sawConnection = false;
    const unsubscribe = subscribeConversationEvents(normalizedCompanyId, {
      onMessage: (payload) => {
        if (!isEnabled || loggedOutRef.current) return;
        if (!payload || typeof payload !== 'object') return;
        onConversationEventRef.current?.(payload);
        handleIncomingPayloadRef.current?.(payload);
      },
      onStatus: (status) => {
        if (status !== 'connected') return;
        const isReconnect = sawConnection;
        sawConnection = true;
        if (!isReconnect && fetchInFlightRef.current) return;
        if (
          !isReconnect &&
          lastFetchCompletedAtRef.current &&
          Date.now() - lastFetchCompletedAtRef.current < WS_RECONNECT_REFETCH_MIN_INTERVAL_MS
        ) {
          return;
        }
        scheduleWsRefetchRef.current?.({
          silent: true,
          forceCustomersRefresh: false,
          invalidateSlotCache: false,
          requireFresh: isReconnect,
        });
      },
    }, {
      source: 'useChatClients',
    });
    wsRef.current = unsubscribe;

    return () => {
      isMounted.current = false;
      cancelRecovery();
      fetchAbortRef.current?.abort();
      clearScheduledWsRefetchRef.current?.();
      closeConversationSubscriptionRef.current?.();
    };
  }, [cancelRecovery, isEnabled, isRealtimeEnabled, normalizedCompanyId]);

  return {
    waitingList,
    attendingList,
    allList,
    clientContacts,
    loading,
    scopeLoading,
    error,
    refetch: fetchConversationsPage,
    applyIncomingPayload: handleIncomingPayload,
    fetchClientContacts, // <-- use esta função ao clicar em um cliente
    setAllList,
  };
};
